import React, { useState } from 'react';
import { devotionals, dailyDevotionals } from '../mock';
import { Download, Mail, Calendar, BookOpen, Mountain, Flower } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Devotionals = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleEmailSubscription = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeMessage('');

    try {
      const response = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubscribeMessage('Welcome to our daily devotional family! Check your email for a welcome message.');
        setEmail('');
      } else {
        setSubscribeMessage(result.error || 'Unable to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscribeMessage('Unable to subscribe right now. Please try again later.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDownload = (version) => {
    // Direct download of the PDFs
    const link = document.createElement('a');
    link.href = version === 'mountain' ? devotionals.mountainVersion : devotionals.floralVersion;
    link.download = `Khesed+Avad-Daily-Devotionals-${version}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="devotionals" className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            Daily Devotionals
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            Start each day with biblical encouragement and practical wisdom. 
            Our devotionals are crafted to bring hope, healing, and spiritual growth to your everyday life.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '40px', marginBottom: '80px' }}>
          {/* Email Subscription Card */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '40px',
            borderRadius: '0px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <Mail size={48} style={{ color: 'var(--text-primary)', marginBottom: '24px' }} />
            <h3 className="heading-2" style={{ marginBottom: '16px' }}>
              Daily Email Devotionals
            </h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
              Receive a daily devotional in your inbox every morning. Start your day with God's Word and encouragement.
            </p>
            
            {subscribeMessage ? (
              <div style={{ 
                color: subscribeMessage.includes('Welcome') ? 'var(--status-success)' : 'var(--status-error)',
                padding: '16px',
                background: 'var(--bg-subtle)',
                borderRadius: '0px',
                marginBottom: '20px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {subscribeMessage}
              </div>
            ) : null}
            
            {!subscribeMessage.includes('Welcome') && (
              <form onSubmit={handleEmailSubscription}>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    disabled={isSubscribing}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      opacity: isSubscribing ? 0.7 : 1
                    }}
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={isSubscribing}
                  style={{ 
                    width: '100%',
                    opacity: isSubscribing ? 0.7 : 1,
                    cursor: isSubscribing ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubscribing ? 'Subscribing...' : 'Subscribe for Free'}
                </button>
              </form>
            )}
          </div>

          {/* Download Options */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '40px',
            borderRadius: '0px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <Download size={48} style={{ color: 'var(--text-primary)', marginBottom: '24px' }} />
            <h3 className="heading-2" style={{ marginBottom: '16px' }}>
              Download Complete Collection
            </h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
              Download our full collection of daily devotionals. Choose your preferred design and keep them forever.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button 
                onClick={() => handleDownload('mountain')}
                className="btn-primary"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Mountain size={18} />
                Mountain Version
              </button>
              
              <button 
                onClick={() => handleDownload('floral')}
                className="btn-primary"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Flower size={18} />
                Floral Version
              </button>
            </div>
          </div>
        </div>

        {/* Sample Devotionals Preview */}
        <div>
          <h3 className="heading-2" style={{ textAlign: 'center', marginBottom: '48px' }}>
            Sample Devotionals
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '32px' }}>
            {dailyDevotionals.map((devotional, index) => (
              <div key={index} style={{
                background: 'var(--bg-primary)',
                padding: '32px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <BookOpen size={20} style={{ color: 'var(--text-secondary)' }} />
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {new Date(devotional.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                
                <h4 className="heading-3" style={{ marginBottom: '16px' }}>
                  {devotional.title}
                </h4>
                
                <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '16px', 
                  borderRadius: '0px',
                  marginBottom: '16px',
                  borderLeft: '4px solid var(--text-primary)'
                }}>
                  <p className="body-small" style={{ 
                    color: 'var(--text-primary)', 
                    fontStyle: 'italic',
                    lineHeight: 1.6
                  }}>
                    {devotional.verse}
                  </p>
                </div>
                
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  {devotional.content}
                </p>
                
                <div style={{ 
                  background: 'var(--bg-subtle)', 
                  padding: '16px', 
                  borderRadius: '0px'
                }}>
                  <p className="body-small" style={{ 
                    color: 'var(--text-primary)', 
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    Prayer:
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    {devotional.prayer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '60px',
          background: 'var(--bg-primary)',
          padding: '40px',
          borderRadius: '0px'
        }}>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Ready to start your daily journey with God? Subscribe above or download the complete collection today.
          </p>
        </div>
      </div>
    </section>
  );
};